import { Box } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { useHistory } from "react-router";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { LeaseContract } from "@/api/generated";
import { PROPERTY_TYPE_VALUES } from "@/api/mappings/property";
import { CustomButton } from "@/components/Button";
import { PropertyEditHistoryRow } from "@/components/property_edit/PropertyEditHistoryRow";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { PropertyManagementDetailSnackbarState, useRequestInitialLoading } from "@/view_models/atoms";
import { SelectedProperty } from "@/view_models/propertySelectors";

import { EditPropertyHistoryItem } from "../../EditPropertyHistoryItem";

import { LeaseContractHistoryEditor } from "./HistoryEdit";

interface props {
  leaseContract: LeaseContract;
  current?: boolean;
}

export const LeaseContractHistoryItem: React.FC<props> = ({ leaseContract, current }) => {
  const history = useHistory();
  const [isShowEdior, setIsShowEditor] = React.useState(false);
  const requestInitialLoading = useRequestInitialLoading();
  // 物件詳細でのスナックバー表示
  const setManagementDetailSnackBarState = useSetRecoilState(PropertyManagementDetailSnackbarState);
  // 選択済み物件取得
  const selected_property = useRecoilValue(SelectedProperty);

  // 編集ボタンクリック時の処理
  const onClickEdit = React.useCallback(() => {
    setIsShowEditor(true);
  }, []);
  // 削除ボタンクリック時の処理
  const onClickDelete = React.useCallback(async () => {
    const fp = PropallyApiFactory();
    try {
      const response = await fp.v1PropertiesPropertyIdLeaseContractsLeaseContractIdDelete(
        selected_property.id,
        leaseContract?.id,
        {
          withCredentials: true,
        }
      );
      if (response.status === 200) {
        requestInitialLoading();
      }
    } catch (e) {
      setManagementDetailSnackBarState({
        message: "物件情報を変更できませんでした",
        status: "error",
        isOpen: true,
      });
      if (selected_property.property.property_type === PROPERTY_TYPE_VALUES.consideration) {
        history.push("/management/consideration/detail");
      } else {
        history.push("/management/own/detail");
      }
    }
  }, [selected_property.id, selected_property.lease_contracts]);

  const historyContents = React.useMemo(() => {
    // 開始日・終了日
    const startToEndMonth = `${dayjs(leaseContract.starts_at).format("YYYY/MM")} ~ ${dayjs(
      leaseContract.ends_at
    ).format("YYYY/MM")}`;
    // 家賃収入
    const monthlyFee = leaseContract?.monthly_fee ? `${leaseContract?.monthly_fee.toLocaleString()}円` : "-円";
    const monthlyManagementFee = leaseContract?.monthly_management_fee
      ? `${leaseContract?.monthly_management_fee.toLocaleString()}円`
      : "-円";
    const otherMonthlyIncome = leaseContract?.other_monthly_income
      ? `${leaseContract?.other_monthly_income.toLocaleString()}円`
      : "-円";

    // 賃貸借契約開始時における収入 / 賃貸借契約開始時における支出
    const typeOfIncomeBeginningContractMap = {
      KeyMoney: "礼金",
      SecurityDeposit: "更新料",
    };
    const typeOfIncomeBeginningContract = leaseContract?.type_of_the_income_of_the_beginning_of_contract
      ? typeOfIncomeBeginningContractMap[leaseContract?.type_of_the_income_of_the_beginning_of_contract]
      : "礼金";
    let income = "-";
    let typeOfOutcomeBeginningContract = "";
    let outcome = "-";
    if (leaseContract?.type_of_the_income_of_the_beginning_of_contract === "KeyMoney") {
      income = `${leaseContract?.key_money.toLocaleString()}ヶ月分`;
      typeOfOutcomeBeginningContract = "新規賃貸契約手数料";
      outcome = leaseContract?.outsourcing_fee_rate_of_new_lease_contract
        ? `${leaseContract.outsourcing_fee_rate_of_new_lease_contract.toLocaleString()}ヶ月分`
        : "-";
    } else if (leaseContract?.type_of_the_income_of_the_beginning_of_contract === "SecurityDeposit") {
      income = `${leaseContract?.security_deposit.toLocaleString()}ヶ月分`;
      typeOfOutcomeBeginningContract = "賃貸契約更新時手数料";
      outcome = leaseContract?.outsourcing_fee_rate_of_updating_lease_contract
        ? `${leaseContract.outsourcing_fee_rate_of_updating_lease_contract.toLocaleString()}ヶ月分`
        : "-";
    }

    const items = [
      { label: "賃貸借契約の開始日・終了日", value: startToEndMonth },
      { label: "家賃収入 (月)", value: monthlyFee },
      { label: "管理費収入 (月)", value: monthlyManagementFee },
      { label: "その他収入 (月)", value: otherMonthlyIncome },
    ];

    // 礼金・更新料が設定されている場合のみ表示
    if (leaseContract?.type_of_the_income_of_the_beginning_of_contract) {
      items.push(
        { label: "賃貸借契約開始時における収入", value: typeOfIncomeBeginningContract },
        { label: typeOfIncomeBeginningContract, value: income }
      );

      // 支出項目も条件に応じて追加
      if (typeOfOutcomeBeginningContract) {
        items.push({ label: typeOfOutcomeBeginningContract, value: outcome });
      }
    }

    return items;
  }, [leaseContract]);

  return (
    <Box>
      {isShowEdior ? (
        <LeaseContractHistoryEditor
          leaseContract={leaseContract}
          onCancel={() => setIsShowEditor(false)}
          onSave={() => setIsShowEditor(false)}
        />
      ) : current ? (
        <Box p={2} borderRadius={8} bgcolor={theme.palette.semantic["surface/base"]}>
          {historyContents.map((item) => (
            <PropertyEditHistoryRow key={item.label} label={item.label} value={item.value} />
          ))}
          <CustomButton type="button" customVariant="outlinedPrimary" buttonSize="M" onClick={onClickEdit}>
            この契約を編集
          </CustomButton>
        </Box>
      ) : (
        <EditPropertyHistoryItem contents={historyContents} onEdit={onClickEdit} onDelete={onClickDelete} />
      )}
      {isShowEdior && (
        <CustomButton type="button" tone="text" onClick={onClickDelete} style={{ color: theme.palette.semantic["status/error-text"], marginTop: 16 }}>
          この記録を削除する
        </CustomButton>
      )}
    </Box>
  );
};
